import { Moment } from "moment";
import jStat from "jstat";
import { TimelineEvent } from "./events";

// times in seconds per status (label) for each item
export type TimesInStatuses = { [itemId: string]: { [labelId: string]: number } };

/**
 *
 * @param events
 * @param columnId column with status
 * @param from
 * @param to
 */
export function timesInStatuses(events: TimelineEvent[], columnId: string, from: Moment, to: Moment): TimesInStatuses {
    const times: TimesInStatuses = {};
    const prevEvents: { [itemId: string]: TimelineEvent } = {};

    const addTime = (itemId: string, labelId: string, timeMs: number): void => {
        times[itemId] = times[itemId] || {};
        times[itemId][labelId] = times[itemId][labelId] || 0;
        times[itemId][labelId] += timeMs;
    };

    // sum all times spent in each column for each item
    events.forEach((e) => {
        if (e.column === columnId && e.date.isSameOrBefore(to)) {
            const prevE = prevEvents[e.item];
            if (prevE !== undefined && e.date.isAfter(from)) {
                if (prevE.date.isAfter(from)) {
                    addTime(e.item, prevE.value, e.date.diff(prevE.date));
                } else {
                    addTime(e.item, prevE.value, e.date.diff(from));
                }
            }
            prevEvents[e.item] = e;
        }
    });

    // add time from last event to now
    for (const itemId in prevEvents) {
        const e = prevEvents[itemId];
        if (e.date.isAfter(from)) {
            addTime(e.item, e.value, to.diff(e.date));
        } else {
            addTime(e.item, e.value, to.diff(from));
        }
    }

    // calculate sums - useful for normalising
    for (const itemId in times) {
        let sum = 0;
        for (const labelId in times[itemId]) {
            sum += times[itemId][labelId];
        }
        times[itemId]["_sum"] = sum;
    }

    return times;
}

export enum StatType {
    MAX,
    SUM,
    MEAN,
    MEDIAN,
    STDEV,
    P80,
    P90,
    HISTOGRAM
}

export type Stats = { [key in StatType]: any };

export function statsForStatuses(
    times: TimesInStatuses,
    selectedStatuses: Set<string>,
    statTypes: Set<StatType>
): Stats {
    const data: number[] = [];

    // sum times for selected statuses for all items
    for (const itemId in times) {
        const sum = Array.from(selectedStatuses).reduce(
            (acc, labelId) => acc + (times[itemId][labelId] !== undefined ? times[itemId][labelId] : 0),
            0
        );
        data.push(sum);
        times[itemId]["_sumSelected"] = sum;
    }

    const calc = jStat(data);
    const stats: Partial<Stats> = {};

    statTypes.forEach((statType) => {
        switch (statType) {
            case StatType.MAX:
                stats[StatType.MAX] = calc.max();
                break;
            case StatType.SUM:
                stats[StatType.SUM] = calc.sum();
                break;
            case StatType.MEAN:
                stats[StatType.MEAN] = calc.mean();
                break;
            case StatType.MEDIAN:
                stats[StatType.MEDIAN] = calc.median();
                break;
            case StatType.STDEV:
                stats[StatType.STDEV] = calc.stdev();
                break;
            case StatType.P80:
                stats[StatType.P80] = jStat.percentile(data, 0.8);
                break;
            case StatType.P90:
                stats[StatType.P90] = jStat.percentile(data, 0.9);
                break;
            case StatType.HISTOGRAM:
                stats[StatType.HISTOGRAM] = calc.histogram(Math.min(20, data.length));
                break;
        }
    });

    return stats as Stats;
}
